/**
 * The firm's API client — profile, settings, branches, policy, integrations,
 * domain, onboarding (spec §A).
 *
 * A client per module, and not a growing `lib/api.ts` (waves.md §1.1): every
 * later wave adds one of these, and one file edited by nine sessions is nine
 * merge conflicts in the file nobody wants a conflict in.
 *
 * Everything goes through `api()` from `../api`, which owns the org header and
 * this app's error envelope. The **one** exception is `coreDomains()` at the
 * bottom, which calls a core service directly — see its own note.
 *
 * The pure helpers here (`stateTone`, `groupCards`, `domainCopy`) live beside
 * the client rather than inside a component so the screens' rules can be
 * tested without rendering anything.
 */

import type {
  ChecklistMark,
  ChecklistMarkState,
  PolicyBasis,
  PolicyDefault,
  PolicySource,
} from './site'
import { APP_SLUG, DOMAINS_API } from '../config'
import { ApiError, ORG_HEADER, api, getOrgId } from '../api'
import { session } from '../session'

// -- shapes ------------------------------------------------------------------

/** ADR-0014's four, and — w13b, ADR-0031 — `other`, which the firm names
 *  itself (`profession_label`) and which carries no conduct policy. */
export type Profession = 'ca' | 'cs' | 'cma' | 'advocate' | 'other'

/** *"What do you do?"* — ADR-0031 §1's limit, the service's too. */
export const PROFESSION_LABEL_MAX = 80

export interface FirmProfile {
  profession: Profession | null
  legal_name: string | null
  display_name: string | null
  logo_url: string | null
  address_line1: string | null
  address_line2: string | null
  city: string | null
  state: string | null
  pincode: string | null
  email: string | null
  mobile: string | null
  pan: string | null
  gstin: string | null
  /** w12a — interface I-1. `AAAA99999A`. */
  tan: string | null
  /** `India` for every firm today; `state` is from the Indian list while it is. */
  country: string
  landline: string | null
  /** `null` = not given, which is not the same as closed all week. */
  working_hours: WorkingHours | null
  about: string | null
  custom_domain: string | null
  website_template: string | null
  colour_scheme: string | null
  onboarding_step: string
  onboarded_at: string | null
  is_onboarded: boolean
  /** w13b — ADR-0031: the firm's own words for its work, required while
   *  `profession` is `other` and its default profession line. Kept (and
   *  ignored) when the firm moves to one of the four. */
  profession_label?: string | null
}

/** Interface I-1's seven keys, Monday first. */
export type Weekday = 'mon' | 'tue' | 'wed' | 'thu' | 'fri' | 'sat' | 'sun'

/** One day's hours — 24-hour `HH:MM`, or `null` for closed. */
export type DayHours = { open: string; close: string } | null

export interface WorkingHours {
  days: Partial<Record<Weekday, DayHours>>
  note: string | null
}

export interface FirmSettings {
  invoice_series_prefix: string
  financial_year_start_month: number
  default_place_of_supply: string | null
  hours_price_invoices: boolean
  review_gate_enabled: boolean
  rounding: string
  approver_roles: Record<string, string>
}

export interface OnboardingState {
  step: string
  steps: string[]
  missing: Record<string, string[]>
  is_onboarded: boolean
  profile?: FirmProfile
  /** The firm's switches, served by every onboarding route so the details step
   *  can **offer** `invoice_series_prefix` rather than let the default apply
   *  unseen (Wave 5 close §3.5). Not every field is asked in onboarding; the
   *  rest are Settings'. */
  settings?: FirmSettings
}

export interface Branch {
  id: string
  code: string
  name: string
  address: string | null
  /** w12a. A name from the Indian list, or null. */
  state: string | null
  is_default: boolean
  status: 'active' | 'archived'
}

/** w12a — one hub card's line (`GET /firm/settings-summary`). `unset` is
 *  *nothing chosen yet*; `attention` is *something to fix*. */
export interface SummaryLine {
  line: string
  tone: 'set' | 'unset' | 'attention'
}

export interface SettingsSummary {
  cards: Record<string, SummaryLine>
  /** Whether the *Setup checklist* card is drawn — only when marks exist. */
  checklist_visible: boolean
  is_onboarded: boolean
}

/** ADR-0029 — one field the platform's organisation fills or differs on. */
export interface OrgSuggestion {
  field: keyof FirmProfile
  label: string
  current: string | null
  suggested: string
  fills_blank: boolean
}

export interface PolicySwitch {
  switch: string
  /** The effective answer — the override if there is one, else the profession
   *  default. `null` only where there is neither, which today means a firm
   *  that has not yet said which profession it is. */
  value: boolean | null
  default: boolean | null
  overridden: boolean
  set_by_user_id: string | null
  set_at: string | null
  /** w3c. ADR-0014 §2 requires the admin to see **the default and its source**
   *  at the moment they override it, so the citation travels with the switch
   *  rather than sitting in a help page the firm would have to go and find.
   *  `null` until the firm has a profession. */
  basis: PolicyBasis | null
  source: PolicyDefault | null
}

/** Re-exported from the site client so the settings screen and the builder
 *  cannot come to describe the same citation two different ways. */
export type { PolicyBasis, PolicyDefault, PolicySource } from './site'
export { BASIS_LABELS } from './site'

/**
 * The spec names three states; `unavailable` is the fourth and it means **we
 * could not ask**. Folding it into `not_offered` would tell a firm the platform
 * does not offer a gateway when the truth is that the read failed — and today
 * that read fails in the live stack, because `integration-service` refuses this
 * app's interim credential (Wave 0B's session note). It is fail-closed either
 * way: never `active`.
 */
export type IntegrationState =
  | 'active'
  | 'available'
  | 'attention'
  | 'not_offered'
  | 'unavailable'

export interface IntegrationCard {
  /** The **provider family** — `payment_gateway`, `email`, `whatsapp`, `sms`,
   *  `email_inbound`. Not a vendor and, since 2026-09-22, no longer one of the
   *  four definition codes this app used to invent. */
  code: string
  name: string
  category: string
  state: IntegrationState
  without_it: string
  /** What is true now — rendered **in place of** `without_it` while the card
   *  is active. A connected card that still explains what happens without it
   *  contradicts its own badge, which is what the live walk found. */
  with_it: string
  /** core's own word for why it is not active — `not_configured`, `disabled`,
   *  `verification_failed`, `entitlement_lapsed` — or one of ours for a read
   *  that did not happen. Reproduced, never renamed: the firm is looking at
   *  the console in the next tab. */
  reason: string | null
  /** The connected connector's definition code, or null. */
  kind: string | null
  /** The vendor, when one is connected. Never a placeholder. */
  provider: string | null
  /** When core's probe last accepted the credential. Null unless active. */
  verified_at: string | null
  since: string | null
}

export interface IntegrationCategory {
  code: string
  label: string
  note: string
}

export interface IntegrationsPayload {
  categories: IntegrationCategory[]
  cards: IntegrationCard[]
  any_unavailable: boolean
  /** Something the firm connected has stopped working. Its own banner. */
  any_attention: boolean
  installable: boolean
}

export type DomainState = 'no_domain' | 'pending' | 'bound'

export interface DomainBinding {
  host: string | null
  state: DomainState
  bound_at: string | null
  /** The live, fail-closed answer. The site and the portal act on this and on
   *  nothing else (ADR-0008 rule 4). */
  can_publish: boolean
}

// -- the client --------------------------------------------------------------

export const firm = {
  read: () =>
    api<{
      profile: Partial<FirmProfile>
      settings: FirmSettings
      onboarding: OnboardingState
      professions: Profession[]
    }>('/api/v1/firm'),

  updateProfile: (body: Partial<FirmProfile>) =>
    api<FirmProfile>('/api/v1/firm/profile', { method: 'PATCH', body }),

  settings: () => api<FirmSettings & { rounding_modes: string[] }>('/api/v1/firm/settings'),
  updateSettings: (body: Partial<FirmSettings>) =>
    api<FirmSettings>('/api/v1/firm/settings', { method: 'PATCH', body }),

  policy: () =>
    api<{
      switches: PolicySwitch[]
      known_switches: string[]
      /** **True from Wave 3C**, and true per firm rather than globally: the
       *  defaults exist for all four professions, so what this reports is
       *  whether *this* firm has said which of the four it is. */
      defaults_available: boolean
      profession: string | null
      regulator: string | null
      read_on: string | null
      sources: PolicySource[]
      disclaimers: { required: boolean; text: string; clause: string | null }[]
      disclaimers_note: string
      /** w13b — `false` for `other` (ADR-0031 §2): no rule applies. `null`
       *  before the firm has a profession; absent from an older service. */
      rules?: boolean | null
    }>('/api/v1/firm/policy'),
  setPolicy: (body: { switch: string; value: boolean | null }) =>
    api<{ switches: PolicySwitch[] }>('/api/v1/firm/policy', { method: 'PUT', body }),

  /** Spec C3. Read on `site:can_view`; set and undone (`state: null`) on
   *  `settings:can_update`. The marks only — whether a row is done by its data
   *  is the Overview's own reads. */
  checklistMarks: () => api<{ marks: ChecklistMark[] }>('/api/v1/firm/checklist'),
  /** Skip setup, or show the list again with `null` — the whole list in one
   *  act (spec C3). `null` clears every mark, which is Settings' way back. */
  skipChecklist: (state: 'skipped' | null) =>
    api<{ marks: ChecklistMark[] }>('/api/v1/firm/checklist', { method: 'PUT', body: { state } }),
  markChecklistRow: (rowKey: string, state: ChecklistMarkState | null) =>
    api<{ marks: ChecklistMark[] }>(`/api/v1/firm/checklist/${rowKey}`, {
      method: 'PUT',
      body: { state },
    }),

  branches: (status: 'active' | 'archived' | 'all' = 'active') =>
    api<Branch[]>(`/api/v1/firm/branches?status=${status}`),
  createBranch: (body: {
    code: string
    name: string
    address?: string | null
    state?: string | null
    is_default?: boolean
  }) => api<Branch>('/api/v1/firm/branches', { method: 'POST', body }),
  updateBranch: (id: string, body: Partial<Branch>) =>
    api<Branch>(`/api/v1/firm/branches/${id}`, { method: 'PATCH', body }),
  /** *Deactivate* on the screen. The default branch is refused (`CONFLICT`). */
  archiveBranch: (id: string) =>
    api<Branch>(`/api/v1/firm/branches/${id}/archive`, { method: 'POST' }),
  /** *Reactivate* — `CONFLICT` when an active branch holds the code. */
  restoreBranch: (id: string) =>
    api<Branch>(`/api/v1/firm/branches/${id}/restore`, { method: 'POST' }),

  /** w12a — one line per hub card, from the service's own reads. */
  settingsSummary: () => api<SettingsSummary>('/api/v1/firm/settings-summary'),
  /** ADR-0029. Fails open on the service: `available: false` and no fields. */
  orgSuggestions: () =>
    api<{ available: boolean; fields: OrgSuggestion[] }>('/api/v1/firm/org-suggestions'),

  integrations: () => api<IntegrationsPayload>('/api/v1/firm/integrations'),

  /** Live and uncached, which makes the read its own re-check: there is
   *  nothing stored to invalidate. */
  domain: () => api<DomainBinding>('/api/v1/firm/domain'),
  /**
   * **Adopt one of the firm's bound domains** — C24 item 16, ADR-0051 §1.
   *
   * The Domain screen has no text box and no Save: it lists what core says is
   * bound and this is what picking one does. `null` stops ProDesk serving
   * one. The **service** verifies the host against core's binding read before
   * storing it, so this is not a free-text claim with an extra step.
   */
  adoptDomain: (host: string | null) =>
    api<DomainBinding>('/api/v1/firm/domain/adopt', { method: 'POST', body: { host } }),

  onboarding: () => api<OnboardingState>('/api/v1/firm/onboarding'),
  /** One step's answers. `settings` carries the few things onboarding asks
   *  that are not profile columns — today only the invoice series prefix — and
   *  is sent in the same request so a step cannot be half-saved. */
  advance: (
    step: string,
    profile: Partial<FirmProfile>,
    settings: Partial<FirmSettings> = {},
  ) =>
    api<OnboardingState>('/api/v1/firm/onboarding', {
      method: 'POST',
      body: { step, profile, settings },
    }),
  complete: () => api<OnboardingState>('/api/v1/firm/onboarding/complete', { method: 'POST' }),
}

// -- core's own domain contract ----------------------------------------------

export interface CoreDnsRecord {
  purpose?: string
  type?: string
  name?: string
  value?: string
  [key: string]: unknown
}

export interface CoreDomain {
  id: string
  hostname: string
  app_slug: string
  state: string
  method?: string
  last_error?: string | null
  instructions?: { records?: CoreDnsRecord[] }
}

/**
 * The firm's domains as **core** holds them — the verification state and the
 * DNS records to publish (FR-A4, dukaan §1's domain step).
 *
 * Why this is not proxied by prodesk-service, in one paragraph so nobody adds
 * a proxy later: `GET /api/v1/domains` is user-scoped — it resolves the caller
 * through org-service and answers for their org — so a proxy would have to
 * impersonate the user to call it. Meanwhile the service-to-service surface
 * our own service *does* use answers for `bound` hosts and 404s every other
 * state, by design, so it cannot show a firm that verification is in progress.
 * The browser has exactly the session this route wants. It reads it.
 *
 * **It degrades rather than fails.** If core is unreachable, or this origin is
 * not on `DOMAINS_CORS_ORIGINS` in some environment, the screen still shows
 * the binding state from our own service and a link to the console — it just
 * cannot show the records inline. Returning `null` rather than throwing is
 * what makes that the ordinary path instead of an error boundary.
 */
export async function coreDomains(): Promise<CoreDomain[] | null> {
  const orgId = getOrgId()
  if (!orgId) return null
  try {
    // `DOMAINS_API` **is** the collection — the edge has already spent the
    // service's `/api/v1/domains` prefix. Appending it again 404s, which the
    // container pass found and this comment exists to stop recurring.
    const res = await session.authed(DOMAINS_API, {
      headers: { [ORG_HEADER]: orgId },
    })
    if (!res.ok) return null
    const body = (await res.json()) as { data?: { domains?: CoreDomain[] } }
    // An org's domains span every app it has bought — this one's list already
    // carries a hostname bound to a sibling product. Only ours are ours.
    return (body.data?.domains ?? []).filter((row) => row.app_slug === APP_SLUG)
  } catch {
    return null
  }
}

// -- pure helpers, so the screens' rules are testable ------------------------

/** The visual weight of a card, and the only place the mapping is written. */
export function stateTone(state: IntegrationState): 'good' | 'neutral' | 'muted' | 'warn' {
  switch (state) {
    case 'active':
      return 'good'
    case 'available':
      return 'neutral'
    case 'not_offered':
      return 'muted'
    // `attention` and `unavailable` are both `warn`, and they are two very
    // different sentences — the label carries the difference, the colour only
    // says "look at this one".
    default:
      return 'warn'
  }
}

export function stateLabel(state: IntegrationState): string {
  switch (state) {
    case 'active':
      return 'Connected'
    case 'available':
      return 'Available'
    case 'attention':
      // Not "Failed": nothing failed, and the firm did nothing wrong. What is
      // true is that core tried the credential and it did not work.
      return 'Not verifying'
    case 'not_offered':
      return 'Not on your plan'
    default:
      return 'Can’t confirm'
  }
}

/**
 * The one line a card leads with: what is true now if it is on, what changes if
 * it is not.
 *
 * Not a ternary at the call site, because getting it the wrong way round is
 * exactly the defect this function was added for — a **Connected** email card
 * that told the firm its mail went out from the platform's address.
 */
export function cardLine(card: IntegrationCard): string {
  return card.state === 'active' && card.with_it ? card.with_it : card.without_it
}

/**
 * The sentence under the badge — what this state means for this firm, today.
 *
 * Contract §6 asks for plain words rather than a status code, and a badge with
 * no consequence is decoration. These are the consequences, and they are here
 * rather than in the component so they can be read against the state list
 * without rendering anything.
 */
export function stateExplanation(card: IntegrationCard): string | null {
  switch (card.state) {
    case 'attention':
      return 'You’ve connected this, but the platform can’t sign in with the details it has. Re-enter them in the console — until then this works as if it weren’t connected at all.'
    case 'not_offered':
      return 'Your plan doesn’t include this connector. It’s a subscription question rather than a switch — nothing in the console will turn it on.'
    case 'unavailable':
      return 'We couldn’t reach the platform to ask. Nothing is treated as connected until we can.'
    case 'available':
      return card.reason === 'disabled'
        ? 'This is connected and switched off in the console.'
        : null
    default:
      return null
  }
}

/**
 * The attention banner's words, naming **what** has stopped working (w11c,
 * T-3.21).
 *
 * The banner used to say one thing whatever had broken — *"your messages still
 * go out, but not from your firm"* — which was true while every connector that
 * could break was a channel. A payment gateway that stops verifying does not
 * send anything from anyone: it takes the **Pay** button away from the firm's
 * clients. A banner that told a firm its messages were fine when its clients
 * could no longer pay would be reassuring about the wrong thing.
 *
 * `null` when nothing needs attention — the banner is not drawn at all.
 */
export function attentionBanner(cards: IntegrationCard[]): string | null {
  const broken = cards.filter((card) => card.state === 'attention')
  if (broken.length === 0) return null
  const gateway = broken.some((card) => card.code === 'payment_gateway')
  const channel = broken.some((card) => card.code !== 'payment_gateway')
  const lines: string[] = []
  if (gateway) {
    lines.push(
      'Your payment gateway isn’t verifying. Until it does, your clients see no Pay button — invoices and hand-recorded receipts still work.',
    )
  }
  if (channel) {
    lines.push(
      gateway
        ? 'A messaging channel you’ve connected isn’t verifying either — your messages still go out, but not from your firm.'
        : 'Something you’ve connected isn’t verifying. Until it does, ProDesk behaves as if it weren’t connected at all — your messages still go out, but not from your firm.',
    )
  }
  return lines.join(' ')
}

/**
 * Whether the card's action is offered.
 *
 * `available` and `attention` can both be acted on, and `attention` is the
 * one that most needs to be: a credential that stopped verifying is fixed in
 * exactly the same place, and a firm shown the problem without the link would
 * have to go and find it. An `active` integration is already on; `not_offered`
 * has nothing in the console to enable, because the answer is a plan rather
 * than a switch; and `unavailable` means we could not ask, so sending the firm
 * to look for a switch that may not be there is worse than saying so.
 *
 * **Nothing is installable into ProDesk** in any case (CLAUDE.md §7, FR-A4,
 * C1) — the action is always a link out to core, which holds the credential
 * and talks to the provider.
 */
export function canEnableInCore(state: IntegrationState): boolean {
  return state === 'available' || state === 'attention'
}

/** Cards grouped in the order the categories were given, empty ones kept —
 *  a category with nothing in it still has an explanation worth reading. */
export function groupCards(
  categories: IntegrationCategory[],
  cards: IntegrationCard[],
): Array<{ category: IntegrationCategory; cards: IntegrationCard[] }> {
  return categories.map((category) => ({
    category,
    cards: cards.filter((card) => card.category === category.code),
  }))
}

/**
 * What the domain step says, and what it lets the firm do.
 *
 * `pending` is ProDesk's word for *a domain named, not yet serving* — the
 * platform's internal surface answers bound-or-nothing, so the app closes the
 * gap with the host the firm gave us. It decorates the screen and gates
 * nothing: `can_publish` is the live read.
 */
export function domainCopy(state: DomainState): { title: string; body: string } {
  switch (state) {
    case 'bound':
      return {
        title: 'Your domain is live',
        body: 'The platform is serving this hostname for ProDesk. Your website and client portal can be published on it.',
      }
    case 'pending':
      return {
        title: 'Waiting for your domain',
        body: 'You have named a domain and the platform is not serving it yet — usually because the DNS records below are not published, or not visible yet. Nothing else in ProDesk is waiting on this.',
      }
    default:
      return {
        title: 'No domain yet',
        body: 'Your website and client portal need your own domain. Everything else — clients, cases, billing — works without one, so this can wait.',
      }
  }
}

/** The onboarding steps' names on screen — interface I-2's seven (w12b, C11,
 *  FR-A9). The order is the service's (`steps` on every onboarding read); this
 *  is only the words. */
export const STEP_LABELS: Record<string, string> = {
  profession: 'Profession',
  firm: 'Your firm',
  address: 'Address & hours',
  billing: 'Billing defaults',
  website: 'Website',
  domain: 'Domain',
  done: 'Done',
}

export function stepIndex(steps: string[], step: string): number {
  const index = steps.indexOf(step)
  return index < 0 ? 0 : index
}

/** The message to show for a refusal, preferring the field list the service
 *  sent over its sentence — "we still need your email" beats "invalid". */
export function refusalDetail(err: unknown): string | null {
  if (!(err instanceof ApiError)) return null
  const missing = err.details.missing
  if (Array.isArray(missing) && missing.length > 0) {
    return `Still needed: ${missing.map((f) => STEP_FIELD_LABELS[String(f)] ?? String(f)).join(', ')}`
  }
  return err.message
}

export const STEP_FIELD_LABELS: Record<string, string> = {
  profession: 'profession',
  profession_label: 'what your firm does',
  legal_name: 'the firm’s legal name',
  email: 'an email address',
  website_template: 'a website template',
}
